// Listing-mode switcher for the Engine Trend module — the MAJOR-module shape
// (src/blank/PATTERNS.md "Module weight").
//
// Engine Trend's two listing views are ROUTES, not local state, mounted as
// siblings under this layout's <Outlet/>:
//
//   <base>/log        → TrendDataGrid
//   <base>/dashboard  → TrendOverview (`<base>/overview` redirects here, H94)
//
// Mirrors src/companies/CompaniesLayout.tsx, which itself mirrors prod's
// InventoryLayout/ComponentLayout breadcrumb dropdown (aeronetsoftware/cloud/
// aeronet-frontend @develop). Replaces a ViewModeHeaderSwitcher that held the
// mode in a `useViewMode` hook, so deep-links and back/forward now work.
//
// LOCAL ADAPTATION vs prod: prod keys its breadcrumb off ABSOLUTE paths
// because those modules mount at a fixed path. This one does not — App.tsx
// routes it at `menuItem.to + "/*"` from the backend nav, at FOUR aliases:
// "trend", "engine-trend", "engine_trend", and "module-placeholder/Trend".
// That last one is TWO segments deep, so any hardcoded base breaks it. The
// base is derived from the live pathname instead, which handles all four.
import { useEffect } from "react";

import { Box, Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";
import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import { migrateModeSegment } from "../components/renamedModeSegments";

/**
 * Mode segment -> dropdown label. Keys are the child route paths, and they are
 * the retired ViewModeHeaderSwitcher's mode keys ("log" / "overview"), with ONE rename:
 * "overview" is now "dashboard" (Aaron 2026-09-28, H94: no "overviews" at a
 * module level). A stored "overview" is migrated on read
 * (readRememberedModeSegment -> migrateModeSegment), so the rename resets no
 * one; any OTHER rename here would, since the stored value is validated
 * against this map.
 */
export const breadcrumbMenu: { [segment: string]: string } = {
  log: "Trend Log",
  dashboard: "Dashboard",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  log: "Engine trend entries",
  dashboard: "By aircraft and PIC",
};

/** Matches the retired switcher's default mode — NOT the dashboard. */
export const DEFAULT_MODE_SEGMENT = "log";

/**
 * Unchanged from the `useViewMode("trend", …)` call this layout replaces. The
 * hook built its key as `${moduleKey}.viewMode` at runtime, so the literal
 * below must keep matching it or every stored mode resets on deploy.
 */
export const MODE_STORAGE_KEY = "trend.viewMode";

/**
 * Is this a real mode segment? Uses hasOwnProperty, NOT `in` — `in` walks the
 * prototype chain, so "constructor" / "toString" / "valueOf" / "__proto__" all
 * test true against an object literal and would pass as valid modes.
 */
const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(breadcrumbMenu, segment);

/** Redirect target for a bare visit to the module root. */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    // "overview" (the dashboard's name until H94) reads as "dashboard".
    stored = migrateModeSegment(window.localStorage.getItem(MODE_STORAGE_KEY));
  } catch {
    // Safari private mode / quota-blocked storage throws on access; a mode
    // redirect is not worth an unhandled exception.
    return DEFAULT_MODE_SEGMENT;
  }
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

interface TrendLayoutProps {
  /** Module title from the nav (e.g. "Engine Trend"); roots the breadcrumb. */
  title?: string;
}

const TrendLayout: React.FC<TrendLayoutProps> = ({ title }) => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "trendBreadcrumbMenu",
  });

  // Split the live pathname so the trailing mode segment can be told apart
  // from the (nav-driven) module base. On "/trend/dashboard" that gives
  // basePath "/trend" and currentMode "dashboard"; on
  // "/module-placeholder/Trend/log" it gives "/module-placeholder/Trend".
  const segments = location.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const currentMode = onMode ? lastSegment : DEFAULT_MODE_SEGMENT;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//log", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "log") and navigates off-site.
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  const basePath = baseSegments.length ? "/" + baseSegments.join("/") : "";

  useEffect(() => {
    if (!onMode) return;
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, lastSegment);
    } catch {
      // Storage unavailable — the route still shows the right mode.
    }
  }, [onMode, lastSegment]);

  return (
    <Box height="100%">
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          <LinkRouter to={basePath || "/"}>
            {title || "Engine Trend"} /
          </LinkRouter>
          <BreadcrumbHoverRoot popupState={popupState}>
            <Button
              endIcon={<BreadcrumbChevronIcon />}
              sx={breadcrumbCurrentTriggerSx}
            >
              <Typography
                variant="h2"
                component="span"
                sx={breadcrumbCurrentLabelSx}
              >
                {breadcrumbMenu[currentMode]}
              </Typography>
            </Button>
            <BreadcrumbDropdownMenu popupState={popupState}>
              {Object.entries(breadcrumbMenu).map(([segment, label]) => (
                <BreadcrumbMenuLinkItem
                  key={segment}
                  to={`${basePath}/${segment}`}
                  label={label}
                  description={describeMode(MODE_DESCRIPTIONS, segment)}
                  selected={currentMode === segment}
                  onClick={popupState.close}
                />
              ))}
            </BreadcrumbDropdownMenu>
          </BreadcrumbHoverRoot>
        </Title>
      </BreadcrumbHeaderListing>
      <Outlet />
    </Box>
  );
};

export default TrendLayout;
